import { AwsUi } from '../../../../core/runtime/src';
import { KeyCode } from '../../../../core/utils/src/dom';
import LegacyClassBuilder from '../../../../core/utils/src/class-builders/legacy';
import debounce from '../../../../core/utils/src/debounce';
import { Input } from '../../../input/src';
import { ComponentFeature } from '../internal';
import { getCollectionComponent, renderSafely, ChangeHandler } from '../utility';
import filteringUtility from './utils';
var __updateController = function (oldValue, newValue) {
    if (oldValue !== newValue && this.__controller) {
        this.__controller.processComponentChange(ComponentFeature.FILTERING, this.__getData(), false);
    }
};
var __handleChange = function () {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
    }
    this.__changeHandler.apply(this, args);
};
var _a = AwsUi.__addComponents(['awsui-table-filtering', 'awsui-cards-filtering'], {
    releaseStatus: 'stable',
    events: {
        // Fired when a change in filtering is triggered by the user.
        // The event `detail` contains the current `filteringText`.
        filteringChange: {
            bubbles: true,
            cancelable: false,
            detailType: 'TableFiltering.FilteringChangeDetail'
        },
        // Fired after the user has changed the value of the filtering input field and stopped typing for a certain
        // period of time.
        // You can use this event instead of `filteringChange` if you do not want to trigger a filtering API call
        // with every keystroke.
        filteringDelayedInput: {
            bubbles: true,
            cancelable: false,
            detailType: 'TableFiltering.FilteringChangeDetail'
        }
    },
    generics: {
        Item: {
            defaultType: 'Table.Item'
        }
    },
    properties: {
        // String that will be used for filtering through the items and filter them.
        // The filtering input will contain this value.
        filteringText: {
            type: 'string',
            optional: true,
            onChange: __handleChange
        },
        // Placeholder for the filtering input.
        filteringPlaceholder: {
            type: 'string',
            optional: true
        },
        // Array of keys within the item object (NOT column ids) whose values are taken into account by the default
        // `filteringFunction`. If set to an empty array, all the properties of the item are taken into account.
        filteringFields: {
            type: 'array',
            itemTypes: ['string'],
            defaultValue: [],
            onChange: __handleChange
        },
        // `(item, filteringText: string, filteringFields): boolean`
        //
        // Function that accepts the following parameters:
        // * `item`: the original item
        // * `filteringText`: text used for filtering
        // * `filteringFields`: specified `filteringFields`.
        //
        // The default value is a function that loops through the `filteringFields` inside the item,
        // ignores all values that are not strings, and matches them against `filteringText`.
        //
        // If you explicitly set this property to `null` no actual filtering is performed on the list
        // of items. Use this option if you want to handle filtering yourself, for example if you want
        // to implement server-side filtering.
        //
        // As a side effect, the `noMatch` region that you can set on Table/Cards component will never
        // be displayed. Use the `empty` region instead.
        filteringFunction: {
            type: 'function',
            functionType: 'TableFiltering.filteringFunction',
            functionGenericType: 'Item',
            defaultValue: filteringUtility.defaultFilteringFunction,
            onChange: __handleChange,
            nullable: true
        },
        // `(resultsCount: integer): string`
        //
        // Given the number of results for the current filtering string, return a
        // human-readable, localized string displaying the number of results. For
        // example, "1 match" or "165 matches."
        //
        // If you set `filteringFunction` to `null` (for example, to implement
        // server-side filtering), this function receives `null` as an argument.
        // However, you should still provide the number of results. If the
        // total number of results is unknown, also include an indication that there
        // may be more results than the number listed. For example, "25+ matches."
        filteringCountTextFunction: {
            type: 'function',
            functionType: 'TableFiltering.filteringCountTextFunction'
        },
        // If set to `true`, the filtering input will be disabled.
        //
        // Use it, for example, if you are fetching new items upon filtering change
        // in order to prevent the user from changing the filtering text.
        disabled: {
            type: 'boolean'
        },
        // Adds an aria label on the built-in filtering input.
        filteringLabel: {
            type: 'string'
        }
    },
    callbacks: {
        initialized: function () {
            var _this = this;
            this.__changeHandler = new ChangeHandler(__updateController);
            this.__notifyDelayedInput = debounce(function () { return _this.__fireEvent('filteringDelayedInput', _this.__getEventData()); });
        },
        attached: function () {
            this.__parentComponent = getCollectionComponent(this.node);
            this.__parentComponent.__filtering = this;
            this.__controller = this.__parentComponent.__controller;
        },
        detached: function () {
            if (this.__parentComponent) {
                this.__parentComponent.__filtering = null;
                this.__controller = null;
            }
        }
    },
    functions: {
        // Will give focus to the underlying input component.
        focus: function () {
            var node = this.node.querySelector('awsui-input');
            if (node) {
                node.component.focus();
            }
        },
        __getData: function () {
            return {
                filteringText: this.filteringText,
                filteringFields: this.filteringFields,
                filteringFunction: this.filteringFunction,
                loading: this.__parentComponent.loading
            };
        },
        __getEventData: function () {
            return {
                filteringText: this.filteringText
            };
        },
        __updateData: function (filteringData, fireEvent) {
            var _this = this;
            this.__changeHandler.prevent(function () {
                _this.filteringText = filteringData.filteringText;
                _this.filteringFields = filteringData.filteringFields;
                _this.filteringFunction = filteringData.filteringFunction;
                _this.loading = filteringData.loading;
            });
            if (fireEvent) {
                this.__fireEvent('filteringChange', this.__getEventData());
                this.__notifyDelayedInput();
            }
        },
        __inputChangeHandler: function () {
            var _this = this;
            return function (event) {
                event.stopPropagation();
                var newData = _this.__getData();
                newData.filteringText = event.detail.value;
                _this.__controller.processComponentChange(ComponentFeature.FILTERING, newData, true);
            };
        },
        __inputKeydownHandler: function () {
            return function (event) {
                // prevent filtering input from accidental form submission
                if (event.detail.keyCode === KeyCode.enter) {
                    event.preventDefault();
                }
            };
        },
        __shouldShowResults: function () {
            if (!this.__controller ||
                !this.filteringText ||
                this.__parentComponent.loading ||
                !this.filteringCountTextFunction) {
                return false;
            }
            return true;
        },
        __resultCount: function () {
            var items = this.__controller.getItems(ComponentFeature.SORTING);
            return items.length;
        },
        __getResultText: function () {
            var argument = this.__resultCount();
            if (!this.filteringFunction) {
                argument = null;
            }
            return this.filteringCountTextFunction(argument);
        }
    },
    render: renderSafely(function (m, h) {
        var showResults = this.__shouldShowResults();
        var cb = new LegacyClassBuilder(this)
            .addComponentClass('container')
            .addComponentClass('full-width', !showResults);
        var filteringResultsClass = 'awsui-filtering-results' + (showResults ? '' : ' awsui-filtering-results__empty');
        return (AwsUi.__createElement("span", { className: cb.toClassName() },
            AwsUi.__createElement(Input, { type: "search", label: this.filteringLabel, value: this.filteringText, input: this.__inputChangeHandler(), keydown: this.__inputKeydownHandler(), placeholder: this.filteringPlaceholder, disabled: this.disabled, ariaDescribedby: null, autocomplete: false }),
            AwsUi.__createElement("span", { "aria-live": "polite", "aria-atomic": "true", className: filteringResultsClass }, showResults ? this.__getResultText() : '')));
    })
}), TableFiltering = _a[0], CardsFiltering = _a[1];
export { TableFiltering, CardsFiltering };
//# sourceMappingURL=index.js.map